<template>
    <v-dialog v-model="dialog" max-width="700px" persistent scrollable>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div class="booking-dialog-title">
                        <v-icon class="mr-2" color="white" size="20">mdi-email-send-outline</v-icon>
                        Send Organiser Login Details
                    </div>
                    <v-btn icon dark @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <v-card-text class="pa-6">
                <div class="booking-section mb-4">
                    <div class="booking-section-header mb-1">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-format-title</v-icon>
                        SUBJECT
                    </div>
                    <v-text-field v-model="subject" outlined dense placeholder="Enter email subject"
                        hide-details></v-text-field>
                </div>
                <div class="booking-section">
                    <div class="booking-section-header mb-2">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-text</v-icon>
                        EMAIL CONTENT
                    </div>
                    <wysiwyg v-model="body" placeholder="Compose the organiser email..." />
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">Cancel</v-btn>
                <v-btn dark color="#3988AA" :loading="sending" :disabled="!subject || !body" @click="sendEmail()">
                    <v-icon small left>mdi-send</v-icon>
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import { brandFor } from "@/utils/brand"
import { currencyFor, balanceDueLabel, paymentDeadlinesHtml, securityDepositHtml, minimumGroupSizeHtml } from "@/utils/paymentTerms"

export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        lead: {
            type: Object,
            default: () => ({})
        },
        event: {
            type: Object,
            default: () => ({})
        },
        company_id: {
            type: Number,
            default: null
        }
    },
    data: () => ({
        subject: '',
        body: '',
        sending: false,
    }),
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        }
    },
    mounted() {
        if (this.show) {
            this.subject = this.buildDefaultSubject()
            this.body = this.buildDefaultBody()
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.sending = false
                this.subject = this.buildDefaultSubject()
                this.body = this.buildDefaultBody()
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close')
        },
        buildDefaultSubject() {
            return `Login details for ${this.event?.name || ''}`
        },
        buildDefaultBody() {
            const lead = this.lead || {}
            const event = this.event || {}
            const companyName = event.company?.company_name || ''
            const advisorName = this.get_user?.name || ''
            const loginLink = `https://client.gogroupbooking.com/${companyName}`
            const signupLink = `${process.env.VUE_APP_CLIENT_URL}${companyName}/signup/${event.id}`

            const termsUrl = brandFor(event.company, event.lead?.event_type || lead.event_type).terms_url
            const termsHtml = termsUrl
                ? `<br><br>You can view our Terms and Conditions for this booking <a href="${termsUrl}">here</a>.`
                : ''
            const deadlinesHtml = paymentDeadlinesHtml(event) + minimumGroupSizeHtml(event) + securityDepositHtml(event.company)
            const currency = currencyFor(event.company)
            const balanceDue = balanceDueLabel(event.company)

            return `
                <div style="text-align:center;font-size:28px;color:black;"><b>Hello ${lead.first_name || ''},</b></div>
                <br>
                <p style="text-align:left;color:black;font-size:16px;">
                    ${advisorName} here.
                    <br><br>
                    Congratulations, your booking has now been secured. Thanks for choosing us to organise your party weekend!
                    <br>
                    Just follow the 3 steps below. If you are unsure of anything, please do not hesitate to ask.
                    <br><br>
                    <b>Step 1: Invite your friends</b>
                    <br>
                    Send your friends this sign-up link so they can join the group and pay:
                    <br>
                    <a href="${signupLink}">${signupLink}</a>
                    <br>
                    This link is for your friends only, as your account is already set up. They only sign up once, then log in through the login link in Step 2 with their own email address.
                    <br><br>
                    <b>Step 2: Log in to your organiser account</b>
                    <br>
                    Everyone in the group, you and your friends, logs in at <a href="${loginLink}">${loginLink}</a>. Log in with your organiser details:
                    <br>
                    Username: ${lead.email || ''}
                    <br>
                    Password: ${event.password || ''}
                    <br>
                    Once logged in, you can also invite friends by clicking "Add Friends", and see who has paid and when, saving you the hassle of chasing money.
                    <br><br>
                    <b>Step 3: Check the itinerary</b>
                    <br>
                    While you're logged in, open the itinerary page to see everything we have arranged for your party weekend, and check the times and dates are correct.
                    <br><br>
                    <b>Payment details</b>
                    <br>
                    Deposit Receipt: A booking deposit has been taken from the card details provided.
                    <br>
                    Invitee Deposits: Your invitees will have 3 weeks from today to secure their place with a ${currency}50 deposit.
                    <br>
                    Full Payment: Full payment for the entire package for your group is required ${balanceDue} in advance of the event date. The pay date is always the Friday ${balanceDue} before the event date, so your whole group is settled up before you arrive at your destination.${termsHtml}${deadlinesHtml}
                    <br><br>
                    It is important to keep regular contact with your group. If there is any change in your group size please contact us as soon as possible so we can update your account.
                </p>
            `
        },
        async sendEmail() {
            this.sending = true
            try {
                const payload = {
                    company_id: this.company_id,
                    lead: this.lead,
                    event: this.event,
                    subject: this.subject,
                    body: this.body,
                    email_type: 'organiser_login_details',
                }
                const result = await (await this.$axios.post('admin/events/send_organiser_email', payload)).data
                if (result.response == true) {
                    this.$toast.success(result.message)
                    this.closeDialog()
                } else {
                    this.$toast.error('Failed to send organiser email')
                }
            } catch (error) {
                this.$toast.error('Failed to send organiser email')
            } finally {
                this.sending = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}
</style>
